"use client";

import notificationIcon from "@/assets/global/notification.svg";
import searchIcon from "@/assets/global/search.svg";
import { SmartImage } from "@/components/ui/smart-image";
import { useLoginModal } from "@/contexts/login-modal-context";
import { useThemeMode } from "@/features/theme/theme-provider";
import { getBrandLogo, getBrandLogoDark } from "@/lib/brand-config";
import { useAuthStore } from "@/store/auth";
import { useUnreadCount } from "@/store/unread-count";
import { useUserInfo } from "@/store/user-info";
import { useAppRouter } from "@/utils/router";
import { formatMonetaryValue } from "@/utils/utils";
import { ArrowLeft } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { memo, useCallback, useMemo } from "react";

interface PageHeaderProps {
    showBackButton?: boolean; // 是否显示返回按钮（替代 Logo）
}

// Logo 组件，使用 memo 避免不必要的重新渲染
const LogoImage = memo( ( { onClick }: { onClick: () => void } ) => {
    const { themeMode } = useThemeMode();
    // 缓存 Logo src，避免每次渲染时重新计算
    const logoSrc = useMemo( () => {
        return themeMode === "dark" ? getBrandLogoDark() : getBrandLogo();
    }, [ themeMode ] );

    return (
        <button type="button" onClick={onClick} className="flex h-[31px] w-[100px] flex-shrink-0 items-center">
            <Image
                src={logoSrc}
                alt="Logo"
                height={21}
                width={100}
                priority
                className="h-[21px] w-[100px] object-contain"
            />
        </button>
    );
} );
LogoImage.displayName = "LogoImage";

function PageHeader( { showBackButton = false }: PageHeaderProps ) {
    const router = useAppRouter();
    const { openModal } = useLoginModal();
    const total = useUnreadCount( ( state ) => state.total );
    const isLogin = useAuthStore( ( state ) => state.isLogin );
    const userAvatar = useUserInfo( ( state ) => state.userAvatar );
    const totalBalance = useUserInfo( ( state ) => state.totalBalance );
    const balanceText = formatMonetaryValue( totalBalance || 0 );

    // 使用 useCallback 缓存 handleBack 函数，避免每次渲染时创建新函数
    const handleBack = useCallback( () => {
        router.replace( "/" );
    }, [ router ] );

    return (
        <div className="bg-surface-raised">
            <div className="mx-auto max-w-[500px] px-4">
                <header className="flex h-14 items-center justify-between pt-3 pb-1">
                    {showBackButton
                        ? (
                            <button onClick={() => router.back()} className="flex-shrink-0 p-1 -ml-1">
                                <ArrowLeft className="w-6 h-6 text-t1" />
                            </button>
                        )
                        : <LogoImage onClick={handleBack} />}

                    <div className="flex items-center gap-2">
                        <button
                            onClick={() => router.push( "/search" )}
                            className="flex size-8 items-center justify-center rounded-full transition-colors"
                            aria-label="Open search"
                        >
                            <SmartImage src={searchIcon} alt="" width={32} height={32} className="size-8 dark:invert" />
                        </button>
                        {isLogin
                            ? (
                                <>
                                    <button
                                        onClick={() => router.push( "/notifications" )}
                                        className="relative flex size-8 items-center justify-center rounded-full transition-colors"
                                        aria-label="Open notifications"
                                    >
                                        <SmartImage
                                            src={notificationIcon}
                                            alt=""
                                            width={32}
                                            height={32}
                                            className="size-8 dark:invert"
                                        />
                                        {total > 0 && (
                                            <span className="absolute right-[6px] top-[6px] size-[6px] rounded-full border border-surface-raised bg-state-danger-strong" />
                                        )}
                                    </button>
                                    <Link href="/me" className="flex size-8 overflow-hidden rounded-full">
                                        <SmartImage
                                            alt="User avatar"
                                            src={userAvatar}
                                            className="size-8 object-cover"
                                            imageUrlOptions={{ width: 120, height: 120 }}
                                        />
                                    </Link>
                                    <button
                                        type="button"
                                        onClick={() => router.push( "/me" )}
                                        className="text-xs font-semibold leading-none text-t1"
                                    >
                                        {balanceText}
                                    </button>
                                </>
                            )
                            : (
                                <button
                                    onClick={() => openModal( "manual" )}
                                    className="h-8 rounded-[8px] bg-action-primary px-3 text-xs font-semibold text-content-fixed-inverse shadow-[inset_0_1px_2px_rgba(0,0,0,0.05),inset_0_-2px_0_rgba(0,0,0,0.05),inset_0_0_0_1px_rgba(0,0,0,0.18)] hover:bg-action-hover transition-colors"
                                >
                                    Sign In/Up
                                </button>
                            )}
                    </div>
                </header>
            </div>
        </div>
    );
}

// 使用 memo 包装组件，避免父组件不必要的重新渲染
export default memo( PageHeader );
